import { Injectable } from '@angular/core'
import { concatMap, from, Observable, switchMap } from 'rxjs'
import { DatasetApiService } from './dataset-api.service'
import { CaseUtilsService } from './case-utils.service'
import { CaseId, SegObjectPropertyMap } from '../../state/case/case.state'
import { ToasterService } from './toaster.service'

@Injectable({
    providedIn: 'root',
})
export class DownloadService {
    constructor(
        private readonly datasetApi: DatasetApiService,
        private readonly caseUtils: CaseUtilsService,
        private readonly toaster: ToasterService
    ) {}

    /** Download one CSV per intensity map (sequential to avoid browser download blocking). */
    downloadMarkers(maps: SegObjectPropertyMap | SegObjectPropertyMap[]): Observable<void> {
        const mapList = (Array.isArray(maps) ? maps : [maps]).filter(m => !!m?.id)
        if (mapList.length === 0) {
            return new Observable(subscriber => {
                this.toaster.error('No intensity map selected for download.', 'Download')
                subscriber.complete()
            })
        }
        return this.caseUtils.createObservableWithCaseId<void>(caseId =>
            from(mapList).pipe(
                concatMap(map => {
                    const filename = this.getFilename(`${caseId}_${map.name}`, 'markers', 'csv')
                    return this.datasetApi
                        .getMarkersBlobForCase(caseId, String(map.id))
                        .pipe(switchMap(blob => this.downloadBlob(filename, blob)))
                })
            )
        )
    }

    downloadSelections(): Observable<void> {
        return this.caseUtils.createObservableWithCaseId<void>(caseId => {
            const filename = this.getFilename(caseId, 'selections', 'json')
            return this.datasetApi
                .getSelectionsBlobForCase(caseId)
                .pipe(switchMap(blob => this.downloadBlob(filename, blob)))
        })
    }

    downloadHierarchicalSelections(name: string): Observable<void> {
        return this.caseUtils.createObservableWithCaseId<void>(caseId => {
            const filename = this.getFilename(caseId, `hierarchical_selections_${name}`, 'json')
            return this.datasetApi
                .getHierarchicalSelectionsBlobForCase(caseId, name)
                .pipe(switchMap(blob => this.downloadBlob(filename, blob)))
        })
    }

    downloadUnsupervisedSelections(name: string): Observable<void> {
        return this.caseUtils.createObservableWithCaseId<void>(caseId => {
            const filename = this.getFilename(caseId, `unsupervised_selections_${name}`, 'json')
            return this.datasetApi
                .getUnsupervisedSelectionsBlobForCase(caseId, name)
                .pipe(switchMap(blob => this.downloadBlob(filename, blob)))
        })
    }

    downloadRecurrentNeighborhood(url: string): Observable<void> {
        return this.caseUtils.createObservableWithCaseId<void>(caseId => {
            const filename = this.getFilename(caseId, 'recurrent_neighborhood', 'zip')
            return this.datasetApi
                .getRecurrentNeighborhoodsResultBlob(url)
                .pipe(switchMap(blob => this.downloadBlob(filename, blob)))
        })
    }

    downloadNeighborhoodEnrichment(url: string): Observable<void> {
        return this.caseUtils.createObservableWithCaseId<void>(caseId => {
            const filename = this.getFilename(caseId, 'neighborhood_enrichment', 'zip')
            return this.datasetApi
                .getNeighborhoodEnrichmentResultBlob(url)
                .pipe(switchMap(blob => this.downloadBlob(filename, blob)))
        })
    }

    downloadAutoCellTypeDetections(url: string): Observable<void> {
        return this.caseUtils.createObservableWithCaseId<void>(caseId => {
            const filename = this.getFilename(caseId, 'auto_cell_type_detections', 'zip')
            const safeUrl = url
                .split('/')
                .map((s, i, a) => (i === a.length - 1 ? encodeURIComponent(s) : s))
            return this.datasetApi
                .getAutoCellDetectionResultBlob(safeUrl.join('/'))
                .pipe(switchMap(blob => this.downloadBlob(filename, blob)))
        })
    }

    downloadSnapshot(width: number, height: number, points: number[]): Observable<void> {
        return this.caseUtils.createObservableWithCaseId<void>(caseId => {
            const filename = this.getFilename(caseId, 'snapshot', 'svg')
            return this.datasetApi.getSnapshotLink(caseId, width, height, points).pipe(
                switchMap(({ url }) => {
                    // make the URL relative (for testing with a proxy, CORS)
                    // url = url.replace('https://spatial.ariadne.ai/', '/')
                    return this.datasetApi
                        .getSnapshotBlob(url)
                        .pipe(switchMap(blob => this.downloadBlob(filename, blob)))
                })
            )
        })
    }

    private downloadBlob(fileName: string, blob: Blob): Observable<void> {
        const a = document.createElement('a')
        const url = window.URL.createObjectURL(blob)
        a.style.display = 'none'
        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        this.toaster.success('Downloaded', fileName)
        return new Observable(() => {
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        })
    }

    private getFilename(caseId: CaseId, dataType: string, fileEnding: string): string {
        return `dataset_${caseId}_${dataType}_${new Date().toJSON().slice(0, 10)}.${fileEnding}`
    }
}
